import React, { Component, SyntheticEvent } from 'react';
import moment from 'moment';
import { Field } from 'bloomer/lib/elements/Form/Field/Field';
import { Label } from 'bloomer/lib/elements/Form/Label';
import { Control } from 'bloomer/lib/elements/Form/Control';
import { Select } from 'bloomer/lib/elements/Form/Select';
import { Button } from 'bloomer/lib/elements/Button';
import api from '../util/api';
import { errorAlert } from '../util/helperFunctions';
import LimitedCharTextArea from '../elements/LimitedCharTextArea';
import Conditional from './Conditional';
import styled from 'styled-components';
import { Box } from 'bloomer/lib/elements/Box';
import Animal from '../Entities/Animal';
import { ASSESSMENT_HOURS_TO_TYPE } from '../constants';

interface PropTypes {
  animal: Animal;
  refresh: () => void;
}

interface StateTypes {
  emoticon: string;
  enrichmentComment: string;
  peeScore: 'none' | number;
  poopScore: 'none' | number;
  isLoading: boolean;
  assessmentResult: 'initial' | 'full' | 'complete' | '';
  assessmentComment: string;
}

const BlackBox = styled<any>(Box)`
  &.box {
    background: black;
    label {
      color: white;
    }
  }
`;

class AnimalSignInForm extends Component<PropTypes, StateTypes> {
  state: StateTypes = {
    emoticon: '',
    enrichmentComment: '',
    peeScore: 'none' as StateTypes['peeScore'],
    poopScore: 'none' as StateTypes['poopScore'],
    isLoading: false,
    assessmentResult: '',
    assessmentComment: '',
  };

  handleSubmit = async (e: SyntheticEvent) => {
    const { animal } = this.props;
    if (!animal.signOutData) {
      console.error('Signed out animal has no sign out data');
      return;
    }

    try {
      e.preventDefault();
      this.setState({
        isLoading: true,
      });
      const { enrichmentComment, emoticon, peeScore, poopScore } = this.state;
      let trainingPlan = 'no';
      if (animal.trainingPlan) {
        trainingPlan = animal.trainingPlan;
      }

      const timeTakenOut = animal.signOutData.timeTakenOut;
      const timeReturned = moment();
      const minutesOfEnrichment = timeReturned.diff(timeTakenOut, 'minutes');

      const postBody: any = {
        animal,
        comment: {
          timeReturned,
          timeTakenOut,
          aNumber: animal.aNumber,
          animalType: animal.animalType,
          comment: enrichmentComment,
          commentEmoticon: emoticon,
        },
        data: {
          minutesOfEnrichment,
          timeReturned,
          timeTakenOut,
          trainingPlan,
          aNumber: animal.aNumber,
          animalName: animal.name,
          animalType: animal.animalType,
          colorCode: animal.colorCode,
          enrichmentType: animal.signOutData.enrichmentType,
          isLongStay: animal.isLongStay,
          locationAtTimeOfEnrichment: animal.getLocationName(),
          walkerName: animal.signOutData.walkerName,
          walkerType: animal.signOutData.walkerType,
        },
        medicalData: {
          peeScore,
          poopScore,
          aNumber: animal.aNumber,
          animalName: animal.name,
          animalType: animal.animalType,
          walkerName: animal.signOutData.walkerName,
          walkerType: animal.signOutData.walkerType,
        },
      };

      if (animal.signOutData.enrichmentType === 'assessment') {
        // Determine how much time is alloted for the assessment to take place
        const { assessmentResult, assessmentComment } = this.state;
        const timeLimit = assessmentResult ? ASSESSMENT_HOURS_TO_TYPE[assessmentResult] : 0;

        const assessedDate = moment().format();
        const assessmentDue = moment(assessedDate) // get duedate in unix format
          .add(timeLimit, 'hours')
          .format();

        postBody.assessment = {
          timeLimit,
          assessmentResult,
          assessmentComment,
          assessedDate,
          assessmentDue,
          animal: this.props.animal,
          previousAssesmentState: this.props.animal.assessmentStatus ? this.props.animal.assessmentStatus : 'initial',
          timeReturned: assessedDate,
        };
      }

      await api.enrichmentData.post(postBody);

      if (this.props.refresh) {
        this.props.refresh();
      }
      // if successful, the component will unmount. No need to update anything else
    } catch (err) {
      errorAlert(err);
      this.setState({
        isLoading: false,
      });
    }
  };

  handleChange = (e: SyntheticEvent) => {
    const target = e.target as HTMLInputElement;
    this.setState({
      [target.name]: target.value,
    } as any);
  };

  render() {
    const { animal } = this.props;
    const { isLoading, assessmentResult } = this.state;
    const isAssessment = animal.signOutData ? animal.signOutData.enrichmentType.toLowerCase() === 'assessment' : false;
    return (
      <BlackBox>
        <form onSubmit={this.handleSubmit} id={`signInForm-${animal.aNumber}`}>
          <Field>
            <Label htmlFor={`enrichmentComment-${animal.aNumber}`}>Do you have any comments?</Label>
            <Control>
              <LimitedCharTextArea
                id={`enrichmentComment-${animal.aNumber}`}
                name="enrichmentComment"
                onChange={this.handleChange}
                limit={300}
              />
            </Control>
          </Field>
          <Conditional check={isAssessment}>
            <>
              <Field>
                <Label htmlFor={`assessmentResult-${animal.aNumber}`}>
                  What are the next steps for assessing this animal?
                </Label>
                <Select
                  id={`assessmentResult-${animal.aNumber}`}
                  name="assessmentResult"
                  onChange={this.handleChange}
                  value={assessmentResult}
                  required={true}
                >
                  <option value=""> - </option>
                  <option value="initial">Needs Temperament Assessment (initial)</option>
                  <option value="full">Needs Full Behaviour Evaluation (full)</option>
                  <option value="complete">No More Assessments Needed (Complete)</option>
                </Select>
              </Field>
              <Field>
                <Label htmlFor={`assessmentComment-${animal.aNumber}`}>
                  What would you like the behaviour team to know about this assessment?
                </Label>
                <Control>
                  <LimitedCharTextArea
                    id={`assessmentComment-${animal.aNumber}`}
                    name="assessmentComment"
                    onChange={this.handleChange}
                    limit={300}
                    required={isAssessment}
                  />
                </Control>
              </Field>
            </>
          </Conditional>
          <Conditional check={animal.animalType === 'cat'}>
            <Field>
              <Label htmlFor={`emoticon-${animal.aNumber}`}>How did your enrichment session go?</Label>
              <Control>
                <Select required={true} onChange={this.handleChange} id={`emoticon-${animal.aNumber}`} name="emoticon">
                  <option value=""> - </option>
                  <option value="0">0. 😄 </option>
                  <option value="1">1. 🙂 </option>
                  <option value="2">2. 😐 </option>
                  <option value="3">3. 😕 </option>
                  <option value="4">4. 😟 </option>
                  <option value="5">5. 😩 </option>
                </Select>
              </Control>
            </Field>
          </Conditional>
          <Conditional check={animal.animalType === 'dog'}>
            <>
              <Field>
                <Label htmlFor={`poopScore-${animal.aNumber}`}>Fecal Score</Label>
                <Control>
                  <Select
                    required={true}
                    onChange={this.handleChange}
                    id={`poopScore-${animal.aNumber}`}
                    name="poopScore"
                  >
                    <option value="none">none</option>
                    <option value="1">1</option>
                    <option value="2">2</option>
                    <option value="3">3</option>
                    <option value="4">4</option>
                    <option value="5">5</option>
                    <option value="6">6</option>
                    <option value="7">7</option>
                  </Select>
                </Control>
              </Field>
              <Field>
                <Label htmlFor={`peeScore-${animal.aNumber}`}>Urine Score</Label>
                <Control>
                  <Select
                    required={true}
                    onChange={this.handleChange}
                    id={`peeScore-${animal.aNumber}`}
                    name="peeScore"
                  >
                    <option value="none">none</option>
                    <option value="normal">Normal</option>
                    <option value="excessive">Excessive</option>
                    <option value="blood">Bloody</option>
                  </Select>
                </Control>
              </Field>
            </>
          </Conditional>
          <Field>
            <Control>
              <Button
                isLoading={isLoading}
                disabled={isLoading}
                type="submit"
                isColor="primary"
                id={`signIn-submit-${animal.aNumber}`}
              >
                Submit
              </Button>
            </Control>
          </Field>
        </form>
      </BlackBox>
    );
  }
}

export default AnimalSignInForm;
